import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { NavLink } from "react-router-dom";
import moment from "moment";

//components
import { Select } from "@gpstrackit/react-controls";
import { Grid } from "@mui/material";
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';

//Notification
import { NotificationManager } from "Components/Notifications";

//Views
import DeleteScheduledAction from 'Modules/scheduledActions/views/components/deleteScheduledAction';
import EditScheduledAction from 'Modules/scheduledActions/views/components/editScheduledAction';

//Actions
import {
    createEditScheduledActionOpenModal,
    scheduledActionsOpenModal,
    searchEntityRequest,
    clearRedux,
} from 'Redux/actions';

//Helpers
import { getDateFormatted, getStatusOptions, getPMUser } from 'Core/data/Helpers';
import { HasPermission } from "Core/security";

const ScheduledActionsTab = (props) => {
    const { height, company } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    //Redux States
    const user = useSelector(state => state.securityRedux.user);
    const editScheduledActionOpenModal = useSelector(state => state.editScheduledActionRedux.openModal);
    const deleteScheduledActionOpenModal = useSelector(state => state.deleteScheduledActionRedux.openModal);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const lang = useSelector(state => state.commonRedux.language);

    const scheduledActionsAccounts = useSelector(state => state.changeStatusAccountRedux.schedule);

    //Variables
    let options = [{ id: "0", label: messages['common_not_executed'] }, { id: "1", label: messages['common_executed'] }]

    //Local States
    const [selectedItems, setSelectedItems] = useState([]);
    const [executed, setExecuted] = useState(options[0]);

    const executedHandler = (exec) => {
        //save preferences
        let currentNewConditions = tableRef?.current?.conditions;
        currentNewConditions = currentNewConditions?.filter((item) => item?.fields[0] !== 'executed');
        if (exec)
            currentNewConditions.push({ fields: ["executed"], terms: [exec.id], conditionOperator: "AND" })
        tableRef?.current?.getData({ newConditions: currentNewConditions });
        setExecuted(exec);
    }

    const [columns, setColumns] = useState([
        {
            key: "entityName",
            field: "entityName",
            title: messages['scheduled_actions_tab_entity_name'],
            width: 250,
            show: true,
            sort: true,
            resize: true,
            export: true,
            functionString: 'return row?.entityName',
            code: "scheduled_actions_tab_entity_name",
            render: (item) => {
                return <span>{item?.entityName}</span>
            }
        },
        {
            key: "name",
            title: messages['scheduled_actions_tab_name'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            functionString: `
                if(row.entityName === 'Unit'){
                    return row?.unit?.label
                }
                if(row.entityName === 'Account'){
                    return row?.company?.name
                }
            `,
            code: "scheduled_actions_tab_name",
            render: (item) => {
                return (
                    <>
                        {
                            item.entityName === "Unit" ? (
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="space-between"
                                    className="context-menu-container"
                                    wrap="nowrap"
                                >
                                    <Grid item>
                                        <NavLink to={`/app/devices/overview/${item?.unit?.id}`} style={{ color: 'black' }}>
                                            <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.unit?.label || "N/A"}</span>
                                        </NavLink>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="space-between"
                                    className="context-menu-container"
                                    wrap="nowrap"
                                >
                                    <Grid item>
                                        <NavLink to={`/app/accounts/overview/${item?.company?.id}`} style={{ color: 'black' }}>
                                            <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.company?.name}</span>
                                        </NavLink>
                                    </Grid>
                                </Grid>
                            )
                        }
                    </>
                )
            }
        },
        {
            key: "action",
            field: "data",
            title: messages['scheduled_actions_tab_action'],
            width: 250,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_action",
            functionString: `
                let label = '';
                let status = ''
                try {
                    let data = JSON.parse(row.data)
                    if (data.newStatusId) {
                        label = "${messages['change_status_title']}"
                        const statusOptions = [
                            {
                                id: 1,
                                label: "${messages['active']}"
                            },
                            {
                                id: 4,
                                label: "${messages['cancelled']}"
                            },
                            {
                                id: 3,
                                label: "${messages['involuntary_suspension']}"
                            },
                            {
                                id: 2,
                                label: "${messages['voluntary_suspension']}"
                            },
                            {
                                id: 6,
                                label: "RMA"
                            }
                        ]

                        let currentStatus = statusOptions.find((item) => item.id === data.newStatusId)
                        if (currentStatus.id) {
                            status = currentStatus.label
                        }
                    }
                }
                catch(error){}

                return label + " - " + status
            `,
            render: (item) => {
                let label = '';
                let status = ''
                try {
                    let data = JSON.parse(item.data)
                    if (data.newStatusId) {
                        label = messages['change_status_title']
                        let currentStatus = getStatusOptions(messages).find((item) => item.id === data.newStatusId)
                        if (currentStatus.id) {
                            status = currentStatus.label
                        }
                    }

                } catch (error) { }
                return <span>{`${label} - ${status}`}</span>
            }
        },
        {
            key: "scheduleOn",
            field: "scheduleOn",
            title: messages['scheduled_actions_tab_scheduled_on'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_scheduled_on",
            functionString: 'return row?.scheduleOn ? moment(row?.scheduleOn).utc().format("MMM DD YYYY") : ""',
            render: (item) => {
                return <span>{item?.scheduleOn ? moment(item?.scheduleOn).utc().format('MMM DD YYYY') : ""}</span>
            }
        },
        {
            key: "executed",
            field: "executed",
            title: messages['scheduled_actions_tab_executed'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_executed",
            functionString: `return row.executed ? "${messages['common_yes']}" : "${messages['common_no']}"`,
            render: (item) => {
                return <span>{item.executed ? messages['common_yes'] : messages['common_no']}</span>
            }
        },
        /* {
            key: "error",
            field: "error",
            title: messages['scheduled_actions_error'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_error",
            functionString: `return row.error`,
            render: (item) => {
                return <span>{item.error || ''}</span>
            }
        }, */
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['scheduled_actions_tab_created_on'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_created_on",
            functionString: `
            function formatDate(date) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
            }
                return row?.createdOn ? formatDate(row?.createdOn) : ''
            `,
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "createdBy",
            field: "createdBy",
            title: messages['scheduled_actions_tab_created_by'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_created_by",
            render: (item) => {
                let label = item?.createdBy ? getPMUser(item?.createdBy, pmUsers) : ""
                return <span>{label}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['scheduled_actions_tab_updated_on'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_updated_on",
            functionString: `
            function formatDate(date) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
            }
            return row?.updatedOn ? formatDate(row?.updatedOn) : ''
            `,
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime")}</span>
            }
        },
        {
            key: "updatedBy",
            field: "updatedBy",
            title: messages['scheduled_actions_tab_updated_by'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_updated_by",
            render: (item) => {
                let label = item?.updatedBy ? getPMUser(item?.updatedBy, pmUsers) : ""
                return <span>{label}</span>
            }
        },
    ])

    useEffect(() => {
        (pmUsers?.length === 0) && dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, [pmUsers]);

    useEffect(() => {
        if (pmUsers.length > 0) {
            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                functionString: item.key === "createdBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
                ` : item.key === "updatedBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
                ` : item.key === "executed" ? `
                    return row.executed ? "${messages['common_yes']}" : "${messages['common_no']}"
                ` : item.key === "action" ? `
                    let label = '';
                    let status = ''
                    try {
                        let data = JSON.parse(row.data)
                        if (data.newStatusId) {
                            label = "${messages['change_status_title']}"
                            const statusOptions = [
                                {
                                    id: 1,
                                    label: "${messages['active']}"
                                },
                                {
                                    id: 4,
                                    label: "${messages['cancelled']}"
                                },
                                {
                                    id: 3,
                                    label: "${messages['involuntary_suspension']}"
                                },
                                {
                                    id: 2,
                                    label: "${messages['voluntary_suspension']}"
                                },
                                {
                                    id: 6,
                                    label: "RMA"
                                }
                            ]

                            let currentStatus = statusOptions.find((item) => item.id === data.newStatusId)
                            if (currentStatus.id) {
                                status = currentStatus.label
                            }
                        }
                    }
                    catch(error){}
                    return label + " - " + status
                ` : item.functionString,
                render: item.key === "createdBy" ? (item) => {
                    let label = item?.createdBy ? getPMUser(item?.createdBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "updatedBy" ? (item) => {
                    let label = item?.updatedBy ? getPMUser(item?.updatedBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "executed" ? (item) => {
                    return <span>{item.executed ? messages['common_yes'] : messages['common_no']}</span>
                } : item.key === "action" ? (item) => {
                    let label = '';
                    let status = '';
                    try {
                        let data = JSON.parse(item.data)
                        if (data.newStatusId) {
                            label = messages['change_status_title']
                            let currentStatus = getStatusOptions(messages).find((item) => item.id === data.newStatusId)
                            if (currentStatus.id) {
                                status = currentStatus.label
                            }
                        }
                    } catch (error) { }
                    return <span>{`${label} - ${status}`}</span>
                } : item.render,
            }))
            setColumns([...currentColumns])

            if (executed) {
                let currentState = options.find((item) => item.id === executed.id)
                if (currentState.id) {
                    setExecuted(currentState)
                }
            }
        }
    }, [pmUsers, lang])

    useEffect(() => {
        if (scheduledActionsAccounts?.response) {
            tableRef && tableRef.current.getData({});
        }
    }, [scheduledActionsAccounts])

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS & CONFIGURATION
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * GraphQL configuration for the DataViewInfiniteScroll
     */
    const graphqlConfig = useMemo(() => ({
        function: "searchScheduledActions",
        sortType: "ScheduledActionSortInput",
        conditionsType: "ScheduledActionSearchInput",
        customQuery: "id company { id name } unit { id label }",
        dynamicQuery: true,
    }), []);

    /**
     * Search configuration
     */
    const searchConfig = useMemo(() => ({
        conditions: ["entityName", "unit_label", "company_name"],
        optionValue: "id",
        optionLabel: "entityName company { name } unit { label }",
        fields: ["entityName", "unit_label", "company_name"],
        fixBar: true,
        custom: { fields: ["companyId"], terms: [company?.id], exact: true },
        openCustomOptions: (value) => {
            if (value && value.entityName) {
                if (value.entityName === "Unit") {
                    return value.unit.label || 'N/A'
                }
                if (value.entityName === "Account") {
                    return value.company.name || 'N/A'
                }
            }
            if (value['entityName company { name } unit { label }']) {
                return value['entityName company { name } unit { label }']
            }
        },
    }), [company?.id]);

    /**
     * Export configuration
     */
    const exportConfig = useMemo(() => ({
        fileName: "scheduledactions",
        /* limit: true, */
    }), []);

    /**
     * UI configuration
     */
    const uiConfig = useMemo(() => ({
        hideSplit: true,
        hideFilters: true,
        hideAllInPagination: true,
        topHeight: props?.single ? 286 : 200,
        height: height && height + "px" || "500px",
    }), []);

    /**
     * Handle condition changes from search/filters
     */
    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        let currentConditions = {}
        let currentTags = {}

        let currentExecuted = executed?.id
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);

            let validateExecuted = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "executed");

            if (validateExecuted?.fields) {
                let currentOption = options.find((item) => item.id === validateExecuted?.terms)
                if (currentOption) {
                    currentExecuted = currentOption?.id
                    setExecuted(currentOption)
                }
            }
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'entityName' && item?.fields[0] !== 'companyId' && item?.fields[0] !== 'executed');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => item['entityName company { name } unit { label }'])
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: ["entityName", "unit_label", "company_name"], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions && currentConditions.push({ fields: ["companyId"], terms: [company?.id], exact: true, conditionOperator: "AND" });
        executed && currentConditions && currentConditions.push({ fields: ["executed"], terms: [currentExecuted || executed?.id || "0"], conditionOperator: "AND" })

        if (!conditions && tags) tableRef?.current?.getData({ newConditions: currentConditions, newColumns });
        else tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions });
    }, [company?.id, executed, options]);

    /**
     * Handle column changes (reorder, resize, visibility)
     */
    const handleChangeColumns = useCallback((items) => setColumns(items), []);

    /**
     * Context menu options for row actions
     */
    const menuOptions = useCallback(() => {
        return [
            messages['scheduled_actions_tab_edit'],
            messages['scheduled_actions_tab_delete'],
        ]
    }, [messages]);

    /**
     * Handle context menu click
     */
    const handleClickMenu = useCallback((event, item) => {
        if (event === messages['scheduled_actions_tab_edit']) {
            if (HasPermission(user, "schact", "u")) {
                if (item.executed) {
                    NotificationManager.warning(
                        messages['scheduled_actions_tab_edit_is_executed'],
                        messages['edit_scheduled_action_title'],
                        5000,
                    );
                } else {
                    dispatch(createEditScheduledActionOpenModal(true, item.id))
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['edit_scheduled_action_title'],
                    5000,
                );
            }
        }
        if (event === messages['scheduled_actions_tab_delete']) {
            if (HasPermission(user, "schact", "d")) {
                if (item.executed) {
                    NotificationManager.warning(
                        messages['scheduled_actions_tab_delete_is_executed'],
                        messages['delete_scheduled_action_title'],
                        5000,
                    );
                } else {
                    dispatch(scheduledActionsOpenModal([item.id]))
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['delete_scheduled_action_title'],
                    5000,
                );
            }
        }
    }, [messages, user, dispatch]);

    /**
     * Bulk action options
     */
    const actionOptions = useMemo(() => [
        {
            id: "edit-scheduled-action",
            label: messages['scheduled_actions_tab_edit'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "schact", "u"),
            action: (items) => {
                let isExecuted = items.some((item) => item.executed)
                if (isExecuted) {
                    NotificationManager.warning(
                        messages['scheduled_actions_tab_edit_is_executed'],
                        messages['edit_scheduled_action_title'],
                        5000,
                    );
                } else {
                    let currentItem = items.find(item => item.id);
                    dispatch(createEditScheduledActionOpenModal(true, currentItem.id))
                }

            }
        },
        {
            id: "delete-scheduled-action",
            label: messages['scheduled_actions_tab_delete'],
            multiple: true,
            show: true,
            disabled: !HasPermission(user, "schact", "d"),
            action: (items) => {
                let isExecuted = items.some((item) => item.executed)
                if (isExecuted) {
                    NotificationManager.warning(
                        messages['scheduled_actions_tab_delete_is_executed'],
                        messages['delete_scheduled_action_title'],
                        5000,
                    );
                } else {
                    let currentItem = items.map(item => item.id);
                    dispatch(scheduledActionsOpenModal(currentItem))
                }
            }
        }
    ], [messages, user, dispatch]);

    /**
     * Extra filters component - renders executed filter
     */
    const renderExtraFilters = useCallback(() => {
        return (
            <div className="extra-filters-tab" style={{ width: '20%', justifyContent: 'end' }}>
                <Select
                    id="executed"
                    placeholder={messages['type']}
                    options={options}
                    value={executed}
                    textField="label"
                    valueField="id"
                    onChange={(items = []) => executedHandler(items)}
                    isClearable
                />
            </div>
        )
    }, [messages, options, executed, executedHandler]);

    // FIX: permanentConditions ensures companyId filter is ALWAYS included in every
    // getData() call (initial, refresh, sort, scroll), even when onChangeConditions
    // hasn't fired yet (no-preferences scenario). This prevents unfiltered queries
    // that would return all scheduled actions across all companies.
    // See: BUG-ANALYSIS-no-preferences-mandatory-conditions.md
    const permanentConditions = useMemo(() => [
        { fields: ["companyId"], terms: [company?.id], exact: true, conditionOperator: "AND" }
    ], [company?.id]);

    return (
        <div className="scheduled-actions-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                columns={columns}
                infiniteScroll={false}
                selectionConfig={{ showCounter: false }}
                permanentConditions={permanentConditions}
                name="pm-scheduled-actions-tab-v2"
                entity="scheduled-actions-tab"
                graphqlConfig={graphqlConfig}
                searchConfig={searchConfig}
                exportConfig={exportConfig}
                uiConfig={uiConfig}
                defaultsConfig={{
                    rowsPerPage: 25,
                    valuesPerPage: [10, 25, 50]
                }}
                noPDF
                onSelectedItems={(items) => { }}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                menuOptions={menuOptions}
                onClickMenu={handleClickMenu}
                options={actionOptions}
                extraFilters={renderExtraFilters}
            />
            {deleteScheduledActionOpenModal && <DeleteScheduledAction tableRef={tableRef} />}
            {editScheduledActionOpenModal && <EditScheduledAction tableRef={tableRef} />}
        </div>
    )
}
export default ScheduledActionsTab;
